import { useState, type FormEvent } from 'react';
import { apiErrorText, type CeoBankAccount, useSetCeoBankBalanceMutation } from '@shared/api';
import { todayIso } from '@shared/lib/localDate';
import { Button } from '@shared/ui/Button';
import { WrapButton } from '../ui';
import { ErrorNotice, FormActions, FormBox, FormGrid, FormHint, FormTitle, Input } from './styles';

interface ManualBalanceFormProps {
  account: CeoBankAccount;
  onClose: () => void;
}

/** Остаток вручную — когда под рукой нет выписки. Операции при этом не появляются. */
export function ManualBalanceForm({ account, onClose }: ManualBalanceFormProps) {
  const [setBalance, { isLoading }] = useSetCeoBankBalanceMutation();
  const [date, setDate] = useState(() => todayIso());
  const [amount, setAmount] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const value = Number(amount.replace(/\s/g, '').replace(',', '.'));
    if (!amount.trim() || !Number.isFinite(value)) {
      setError('Укажите остаток числом');
      return;
    }
    try {
      setError(null);
      const res = await setBalance({
        id: account.id,
        snapshot_date: date,
        closing_balance: value,
      }).unwrap();
      // Остаток за прошлую дату ушёл в историю, а текущий не изменился: человек должен это видеть.
      if (res.warnings.length > 0) {
        setError(res.warnings.join(' '));
        return;
      }
      onClose();
    } catch (err: unknown) {
      setError(apiErrorText(err, 'Не удалось сохранить остаток'));
    }
  }

  return (
    <FormBox as="form" onSubmit={handleSubmit}>
      <FormTitle>
        Остаток на конец дня: {account.bank_name}, счёт {account.account_number}
      </FormTitle>
      <FormHint>
        Подойдёт, когда под рукой нет выписки. Операции так не появятся: для сверки платежей лучше
        загрузить выписку файлом.
      </FormHint>
      <FormGrid>
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
        <Input
          placeholder="Остаток, ₽"
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          required
        />
      </FormGrid>
      {error && <ErrorNotice>{error}</ErrorNotice>}
      <FormActions>
        <WrapButton $size="sm" type="submit" disabled={isLoading}>
          {isLoading ? 'Сохранение…' : 'Сохранить остаток'}
        </WrapButton>
        <Button $size="sm" $variant="ghost" type="button" onClick={onClose}>
          Отмена
        </Button>
      </FormActions>
    </FormBox>
  );
}
